import { FirebaseApp } from 'firebase/app'
import emitter from '@/services/infrastructure/emitter'
import { AUTH_STATE_CHANGE } from '@/types/events'
import { AuthState } from '@tritt/firebase-api'
import { computed, ref } from 'vue'
import { AuthServices } from '@tritt/firebase-client'
import { AuthAdapter, MockAuthAdapter, ContaAuthAdapter } from './auth'

// Reactive auth state
export const user = ref<AuthState | null>(null)
export const getUser = computed(() => user.value)
export const isLoggedIn = computed(() => !!user.value)

// Auth initialization promise
let authStateInitializeResolve: (value: unknown) => void
export const authStateInitialized = new Promise(resolve => {
    authStateInitializeResolve = resolve
})

// Current adapter instance
let adapter: AuthAdapter

/**
 * Get the current auth adapter instance.
 * Useful for accessing adapter-specific methods.
 */
export const getAdapter = () => adapter

/**
 * Get access token for API calls.
 */
export const getAccessToken = async (): Promise<string | undefined> => {
    return adapter?.getAccessToken()
}

/**
 * Initialize the auth layer.
 * 
 * Selects adapter based on `window.useMockBackend`:
 * - If true: Uses MockAuthAdapter (no Firebase, immediate state injection)
 * - If false: Uses ContaAuthAdapter (real Firebase/Tritt auth)
 */
const init = async (app: FirebaseApp, authServices: AuthServices) => {
    // Select adapter based on mock flag
    if (window.useMockBackend) {
        console.log('[auth.service] Using MockAuthAdapter')
        adapter = new MockAuthAdapter()
    } else {
        console.log('[auth.service] Using ContaAuthAdapter')
        const contaAdapter = new ContaAuthAdapter()
        contaAdapter.configure(app, authServices)
        adapter = contaAdapter
    }

    // Initialize the adapter FIRST (this reads cyInitialAuth in mock mode)
    await adapter.init()

    // THEN subscribe to auth state changes (callback will fire with initial state)
    adapter.onAuthStateChange((authState) => {
        user.value = authState
        emitter.emit(AUTH_STATE_CHANGE, authState)

        // @ts-ignore - Expose userId for Cypress
        if (window.Cypress) {
            // @ts-ignore
            window.userId = authState?.uid
        }
    })

    // Resolve the initialization promise
    authStateInitializeResolve(undefined)

    // Expose force refresh for network recovery
    // @ts-ignore
    window.forceRefreshToken = async () => {
        if (adapter instanceof ContaAuthAdapter) {
            await adapter.forceRefreshToken()
        }
    }
}

/**
 * Sign in with username/password.
 */
const signInWithUsernameAndPassword = async (username: string, password: string) => {
    return adapter.login(username, password)
}

/**
 * Sign in with Google popup. Is this in use or can we delete?
 */
const signInWithGooglePopup = async () => {
    if (adapter instanceof ContaAuthAdapter) {
        return adapter.signInWithGooglePopup()
    }
    // Mock mode doesn't support Google popup
    console.warn('[auth.service] Google popup not available in mock mode')
}

/**
 * Logout.
 */
const logout = async () => {
    return adapter.logout()
}

// Network status handlers
window.addEventListener('offline', () => {
    console.warn('[auth.service] App is offline')
})

window.addEventListener('online', async () => {
    if (adapter instanceof ContaAuthAdapter) {
        await adapter.forceRefreshToken()
    }
})

/**
 * Composable for using the auth service.
 */
const useFirebaseAuth = () => {
    return {
        init,
        signInWithUsernameAndPassword,
        signInWithGooglePopup,
        logout
    }
}

export {
    useFirebaseAuth
}